// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * Pure derivation of the reference overlay from a cycle snapshot. The
 * fitness overlay (dotted level line, min–max band, delta annotations)
 * and the cost-tab level line both render from this single selector —
 * nothing recomputes from live agents. Everything derives from
 * `dto.reference_set` plus the score series already in the snapshot.
 *
 * `undefined` reference set (feature off / pre-feature snapshot) →
 * `null`, so both charts render exactly today's dashboard.
 */

/** Reference level + band + deltas the fitness/cost overlays render. */
export interface ReferenceSeries {
  /** N — the disclosed reference-agent count (INV-3). */
  n: number
  /** The reference level: `max(reference_set.scores)` (INV-3). */
  max: number
  /** Distribution floor — the min–max band's lower edge. */
  min: number
  /** Median of the reference scores (side-block stat, reused downstream). */
  median: number
  /** `true` when N > 1 — the min–max band renders (AC2). */
  hasBand: boolean
  /** Per-run execution cost of the reference agents — the cost-tab line. */
  costPerRun: number
  /** Disclosed line label, e.g. `reference — best of 5` (AC1, INV-3). */
  label: string
  /**
   * "Architects' gain": best gen 0 − reference max. `null` when the
   * snapshot has no scored iteration yet (nothing to compare against).
   */
  architectsGain: number | null
  /** "Evolution's gain": best final − best gen 0. `null` as above. */
  evolutionGain: number | null
}

interface WithReferenceSet {
  reference_set?: ReferenceSet
  all_scores_by_iter: number[][]
}

/** The reference cost/run overlay line for the cost tab (AC4). */
export interface ReferenceCostLine {
  /** The references' per-run execution cost — the level line's value. */
  costPerRun: number
  /** N — disclosed alongside the line. */
  n: number
  /** `$`-formatted cost/run, e.g. `$0.0004` (sub-cent precision). */
  formatted: string
}

/**
 * Format a per-run cost. `formatCurrency` caps at 2 decimals, which
 * collapses a sub-cent cost/run to `$0.00`; the reference cost/run is
 * typically fractions of a cent, so it keeps up to 4 fractional digits
 * (2 for cent-scale-or-larger values). Pure `$` figure — no score/€
 * normalization (INV-8).
 */
export const formatCostPerRun = (n: number): string => {
  const digits = Math.abs(n) >= 0.01 || n === 0 ? 2 : 4
  return `$${n.toFixed(digits)}`
}

/**
 * Derive the cost-tab reference line from `reference_set.cost_per_run`.
 * `undefined` / empty reference set → `null` (no line, cost tab renders
 * exactly today's dashboard, INV-6). Independent of the score series —
 * the cost tab carries no iteration axis.
 */
export const deriveReferenceCostLine = (dto: {
  reference_set?: ReferenceSet
}): ReferenceCostLine | null => {
  const ref = dto.reference_set
  if (ref === undefined || ref.scores.length === 0) return null
  return {
    costPerRun: ref.cost_per_run,
    n: ref.scores.length,
    formatted: formatCostPerRun(ref.cost_per_run),
  }
}

const median = (sorted: readonly number[]): number => {
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid]
}

/** N/max/min/median of the reference scores — the shared stat block. */
export interface ReferenceStats {
  n: number
  max: number
  min: number
  median: number
  /** `true` when N > 1 — the min–max band renders (AC2). */
  hasBand: boolean
}

/**
 * The single derivation of the reference distribution stats, reused by
 * both the fitness/cost overlay (`deriveReferenceSeries`) and the side
 * block (`deriveReferenceBlock`) so N/max/min/median come from one
 * source (INV-10). `scores` is assumed non-empty (callers gate the
 * absent / empty case to `null` first).
 */
export const deriveReferenceStats = (
  scores: readonly number[],
): ReferenceStats => {
  const sorted = [...scores].sort((a, b) => a - b)
  const n = sorted.length
  return {
    n,
    max: sorted[n - 1],
    min: sorted[0],
    median: median(sorted),
    hasBand: n > 1,
  }
}

/** Highest score in `scores`, or `null` when empty. */
export const bestOf = (scores: readonly number[]): number | null => {
  if (scores.length === 0) return null
  let best = scores[0]
  for (let i = 1; i < scores.length; i += 1) {
    if (scores[i] > best) best = scores[i]
  }
  return best
}

/** Best score of the first / last iteration that has any score. */
const bestGenBounds = (
  allScoresByIter: readonly (readonly number[])[],
): { first: number | null; last: number | null } => {
  let first: number | null = null
  let last: number | null = null
  for (const scores of allScoresByIter) {
    const best = bestOf(scores)
    if (best === null) continue
    if (first === null) first = best
    last = best
  }
  return { first, last }
}

export const deriveReferenceSeries = (
  dto: WithReferenceSet,
): ReferenceSeries | null => {
  const ref = dto.reference_set
  if (ref === undefined || ref.scores.length === 0) return null

  const { n, max, min, median, hasBand } = deriveReferenceStats(ref.scores)

  const { first: bestGen0, last: bestFinal } = bestGenBounds(
    dto.all_scores_by_iter,
  )

  return {
    n,
    max,
    min,
    median,
    hasBand,
    costPerRun: ref.cost_per_run,
    label: `reference — best of ${n}`,
    architectsGain: bestGen0 === null ? null : bestGen0 - max,
    evolutionGain:
      bestGen0 === null || bestFinal === null ? null : bestFinal - bestGen0,
  }
}
